import * as yup from 'yup';

import i18n from 'config/i18n';
import { getLocale } from 'utils/date';
import {
  findOverlappingPhases,
  MAX_PROTOCOL_WEEKS
} from './ProtocolRuleForm.utils';

import type { ActivityResponse, RecurrenceType } from 'generated/index';
import type { ProtocolRulePhaseValues } from './ProtocolRuleForm.types';

const t = i18n.getFixedT(getLocale());
const key = (name: string) => `adminPortal.protocols.ruleForm.${name}`;

const phaseSchema = yup.object({
  startWeek: yup
    .number()
    .nullable()
    .required(t(key('startInvalid')))
    .integer(t(key('wholeNumber')))
    .min(1, t(key('startInvalid'))),
  endWeek: yup
    .number()
    .nullable()
    .required(t(key('endRequired')))
    .integer(t(key('wholeNumber')))
    .test(
      'not-before-start',
      t(key('endBeforeStart')),
      (endWeek, { parent }) =>
        endWeek == null ||
        parent.startWeek == null ||
        endWeek >= parent.startWeek
    )
    .max(MAX_PROTOCOL_WEEKS, t(key('endTooLate'), { max: MAX_PROTOCOL_WEEKS })),
  recurrenceType: yup
    .mixed<RecurrenceType>()
    .oneOf(['WEEKLY', 'TOTAL'])
    .required(),
  amount: yup
    .number()
    .nullable()
    .required(t(key('amountInvalid')))
    .integer(t(key('wholeNumber')))
    .min(1, t(key('amountInvalid'))),
  interval: yup
    .number()
    .nullable()
    .when('recurrenceType', {
      is: 'WEEKLY',
      then: (interval) =>
        interval
          .integer(t(key('wholeNumber')))
          .required(t(key('intervalInvalid')))
          .min(1, t(key('intervalInvalid')))
    })
});

/**
 * The same checks as the backend's ProtocolRuleValidation, so a bad rule is caught before saving; an
 * overlap is reported at every later phase of an overlapping pair.
 */
const schema = yup.object({
  activity: yup
    .mixed<ActivityResponse>()
    .nullable()
    .required(t(key('activityRequired'))),
  phases: yup
    .array(phaseSchema)
    .min(1)
    .required()
    .test('no-overlap', t(key('overlap')), (phases, context) => {
      const overlapping = findOverlappingPhases(
        (phases ?? []) as ProtocolRulePhaseValues[]
      );
      if (overlapping.length === 0) {
        return true;
      }
      return new yup.ValidationError(
        overlapping.map((index) =>
          context.createError({
            path: `phases[${index}].startWeek`,
            message: t(key('overlap'))
          })
        )
      );
    }),
  description: yup.string().defined()
});

export default schema;
